/**
 * Pure functions for validating files BEFORE they are read or parsed.
 * Runs synchronously at the moment of file selection — no async work.
 */

export type ExtensionCheck =
  | { kind: 'ok' }
  | { kind: 'bad_extension'; extension: string; allowed: string[] };

export type SizeCheck =
  | { kind: 'ok' }
  | { kind: 'too_large'; sizeBytes: number; maxBytes: number }
  | { kind: 'empty' };

export type FileCheck =
  | { kind: 'ok' }
  | { kind: 'bad_extension'; extension: string; allowed: string[] }
  | { kind: 'too_large'; sizeBytes: number; maxBytes: number }
  | { kind: 'empty' };

const getExtension = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.substring(idx).toLowerCase() : '';
};

export const checkExtension = (file: File, allowed: string[]): ExtensionCheck => {
  const ext = getExtension(file.name);
  const normalized = allowed.map((a) => a.toLowerCase());
  if (!ext || !normalized.includes(ext)) {
    return { kind: 'bad_extension', extension: ext || '(none)', allowed };
  }
  return { kind: 'ok' };
};

export const checkSize = (file: File, maxBytes: number): SizeCheck => {
  if (file.size === 0) return { kind: 'empty' };
  if (file.size > maxBytes) return { kind: 'too_large', sizeBytes: file.size, maxBytes };
  return { kind: 'ok' };
};

/**
 * Combined check. Extension is checked first; if it fails the size is not checked.
 */
export const checkFile = (file: File, allowedExtensions: string[], maxBytes: number): FileCheck => {
  const ext = checkExtension(file, allowedExtensions);
  if (ext.kind !== 'ok') return ext;
  const size = checkSize(file, maxBytes);
  if (size.kind !== 'ok') return size;
  return { kind: 'ok' };
};
